import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { ASSETS, SITE } from "@/constants/site";

const PRIMARY_NAV = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/services", label: "Services" },
    { to: "/new-construction", label: "New Construction" },
    { to: "/nonprofit", label: "Nonprofit" },
    { to: "/impact", label: "Impact" },
    { to: "/collaborations", label: "Partners" },
    { to: "/contact", label: "Contact" },
];

const MORE_NAV = [
    { to: "/media", label: "Media" },
    { to: "/blog", label: "Insights" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [moreOpen, setMoreOpen] = useState(false);
    const moreRef = useRef(null);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 16);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        setMoreOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (moreRef.current && !moreRef.current.contains(e.target)) {
                setMoreOpen(false);
            }
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    const moreIsActive = MORE_NAV.some((n) => location.pathname.startsWith(n.to));

    return (
        <header
            data-testid="site-header"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-white/95 backdrop-blur border-b border-slate-200"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="nav-logo-link"
                    className="flex items-center gap-3"
                >
                    <img
                        src={ASSETS.logo}
                        alt="Hopewell Consulting LLC"
                        className="h-12 w-auto"
                    />
                    <span className="hidden sm:flex flex-col leading-tight">
                        <span className="font-serif text-lg font-bold text-[#0A192F]">
                            Hopewell Consulting
                        </span>
                        <span className="text-[10px] tracking-[0.25em] uppercase text-slate-500">
                            Knoxville, TN
                        </span>
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-7">
                    {PRIMARY_NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.to === "/"}
                            data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `text-sm font-medium tracking-wide transition-colors ${
                                    isActive
                                        ? "text-[#0A192F]"
                                        : "text-slate-600 hover:text-[#0A192F]"
                                }`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}

                    {/* More dropdown */}
                    <div className="relative" ref={moreRef}>
                        <button
                            type="button"
                            data-testid="nav-more-toggle"
                            onClick={() => setMoreOpen((o) => !o)}
                            className={`flex items-center gap-1 text-sm font-medium tracking-wide transition-colors ${
                                moreIsActive
                                    ? "text-[#0A192F]"
                                    : "text-slate-600 hover:text-[#0A192F]"
                            }`}
                            aria-expanded={moreOpen}
                            aria-haspopup="true"
                        >
                            More
                            <ChevronDown
                                className={`w-3.5 h-3.5 transition-transform ${moreOpen ? "rotate-180" : ""}`}
                            />
                        </button>
                        {moreOpen && (
                            <div
                                data-testid="nav-more-dropdown"
                                className="absolute right-0 mt-4 w-52 bg-white border border-slate-200 shadow-xl rounded-sm py-2"
                            >
                                {MORE_NAV.map((n) => (
                                    <NavLink
                                        key={n.to}
                                        to={n.to}
                                        data-testid={`nav-dropdown-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                                        className={({ isActive }) =>
                                            `block px-5 py-2.5 text-sm font-medium transition-colors ${
                                                isActive
                                                    ? "text-[#D4AF37] bg-slate-50"
                                                    : "text-slate-700 hover:bg-slate-50 hover:text-[#0A192F]"
                                            }`
                                        }
                                    >
                                        {n.label}
                                    </NavLink>
                                ))}
                            </div>
                        )}
                    </div>
                </nav>

                <div className="hidden lg:flex items-center gap-4">
                    <a
                        href={`tel:${SITE.phoneLink}`}
                        data-testid="nav-phone-link"
                        className="flex items-center gap-2 text-sm font-semibold text-[#0A192F] hover:text-[#D4AF37] transition-colors"
                    >
                        <Phone className="w-4 h-4" /> {SITE.phone}
                    </a>
                    <Link
                        to="/contact"
                        data-testid="nav-cta-button"
                        className="bg-[#0A192F] text-white hover:bg-[#1E3A8A] transition-colors rounded-sm px-5 py-3 text-xs font-bold uppercase tracking-widest"
                    >
                        Schedule
                    </Link>
                </div>

                <button
                    type="button"
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen((o) => !o)}
                    className="lg:hidden p-2 text-[#0A192F]"
                    aria-label="Toggle menu"
                >
                    {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden bg-white border-t border-slate-200"
                >
                    <div className="px-6 py-6 flex flex-col gap-4">
                        {PRIMARY_NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.to === "/"}
                                data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `text-sm font-medium ${isActive ? "text-[#D4AF37]" : "text-slate-700"}`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                        <div className="pt-3 mt-1 border-t border-slate-100">
                            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400 mb-3">
                                More
                            </p>
                            <div className="flex flex-col gap-3">
                                {MORE_NAV.map((n) => (
                                    <NavLink
                                        key={n.to}
                                        to={n.to}
                                        data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                                        className={({ isActive }) =>
                                            `text-sm font-medium ${isActive ? "text-[#D4AF37]" : "text-slate-700"}`
                                        }
                                    >
                                        {n.label}
                                    </NavLink>
                                ))}
                            </div>
                        </div>
                        <a
                            href={`tel:${SITE.phoneLink}`}
                            data-testid="mobile-phone-link"
                            className="flex items-center gap-2 text-sm font-semibold text-[#0A192F] pt-3 mt-1 border-t border-slate-100"
                        >
                            <Phone className="w-4 h-4" /> {SITE.phone}
                        </a>
                        <Link
                            to="/contact"
                            data-testid="mobile-cta-button"
                            className="bg-[#0A192F] text-white text-center rounded-sm px-5 py-3 text-xs font-bold uppercase tracking-widest"
                        >
                            Schedule a Consultation
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
